import { useMemo, useCallback } from 'react';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import { AccessTokenModalType } from '../../constants/access-token';
import {
	ASANA,
	MONDAY,
	type SupportedThirdParty,
	TRELLO,
	JIRA_SOFTWARE,
	CLICK_UP,
	JIRA_BUSINESS,
} from '../../constants/import-type';

import type { RequestAccessTokenMethod } from '../../types';
import { AsanaClient } from './asana';
import { ClickupClient } from './clickup';
import { JiraClient } from './jira';
import { MondayClient } from './monday';
import { NotSupportedClient } from './not-supported';
import { TrelloClient } from './trello';
import type { ThirdPartyClient } from './types';

export const useGetThirdPartyClient = ({
	requestAuthenticationToken,
}: {
	requestAuthenticationToken?: RequestAccessTokenMethod;
} = {}): ((thirdParty: SupportedThirdParty) => ThirdPartyClient) => {
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();

	return useCallback(
		(thirdParty: SupportedThirdParty) => {
			switch (thirdParty) {
				case ASANA:
					return new AsanaClient({ cloudId, formatMessage });
				case TRELLO:
					return new TrelloClient({ cloudId });
				case MONDAY:
					return new MondayClient({
						cloudId,
						formatMessage,
						requestAuthenticationToken: requestAuthenticationToken
							? () =>
									requestAuthenticationToken({
										thirdParty: MONDAY,
										modalType: AccessTokenModalType.EXPIRED_TOKEN,
										shouldCloseAndReturnToUploadStep: true,
									})
							: undefined,
					});
				case JIRA_SOFTWARE:
					return new JiraClient({ cloudId });
				case JIRA_BUSINESS:
					if (ff('jira-business-to-software-project-imports')) {
						return new JiraClient({ cloudId, thirdPartyType: JIRA_BUSINESS });
					}
					return new NotSupportedClient(thirdParty);
				case CLICK_UP:
					return new ClickupClient({ cloudId, formatMessage });
				default:
					return new NotSupportedClient(thirdParty);
			}
		},
		[cloudId, formatMessage, requestAuthenticationToken],
	);
};

export const useThirdPartyClient = ({
	thirdParty,
	requestAuthenticationToken,
}: {
	thirdParty?: SupportedThirdParty;
	requestAuthenticationToken?: RequestAccessTokenMethod;
} = {}): ThirdPartyClient => {
	const getThirdPartyClient = useGetThirdPartyClient({ requestAuthenticationToken });

	return useMemo(
		() => (thirdParty ? getThirdPartyClient(thirdParty) : new NotSupportedClient(thirdParty)),
		[getThirdPartyClient, thirdParty],
	);
};
